import { Form, Input, InputNumber, Switch, Button, Card, Row, Col, Space, Divider, Select } from 'antd'
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons'
import React from 'react'
import { GlobalServicesEnums } from '../utils/enums'

const { TextArea } = Input
const { Option } = Select

const GlobalServicesForm = ({ currentRecord, handleGlobalServicesApi, setFacet, formLoading }) => {
  const [form] = Form.useForm()

  // Ensure numeric fields are properly converted
  const getInitialValues = () => {
    if (!currentRecord) return {}

    return {
      ...currentRecord,
      defaultRate: currentRecord.defaultRate ? Number(currentRecord.defaultRate) : undefined,
      defaultDurationMin: currentRecord.defaultDurationMin ? Number(currentRecord.defaultDurationMin) : undefined,
      active: currentRecord.active !== undefined ? currentRecord.active : true
    }
  }

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      active: values.active || false,
      defaultRate: Number(values.defaultRate),
      defaultDurationMin: Number(values.defaultDurationMin)
    }

    try {
      if (currentRecord?.isNew) {
        await handleGlobalServicesApi(GlobalServicesEnums.routeKeys.CREATE, { data: payload })
      } else {
        await handleGlobalServicesApi(GlobalServicesEnums.routeKeys.UPDATE_BY_ID, {
          data: payload,
          recordId: currentRecord._id
        })
      }
    } catch (error) {
      console.error('Form submission error:', error)
    }
  }

  const handleCancel = () => {
    setFacet(1) // Return to table view
  }

  return (
    <Card
      title={
        <Space>
          <Button type='text' icon={<ArrowLeftOutlined />} onClick={handleCancel}>
            Back to Services
          </Button>
          <Divider type='vertical' />
          {currentRecord?.isNew ? 'Add New Service' : 'Edit Service'}
        </Space>
      }
    >
      <Form form={form} layout='vertical' initialValues={getInitialValues()} onFinish={handleSubmit} size='large'>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label='Service Name'
              name='name'
              rules={[
                { required: true, message: 'Please enter service name' },
                { min: 2, message: 'Service name must be at least 2 characters' }
              ]}
            >
              <Input placeholder='Enter service name' />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label='Category'
              name='category'
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <Select
                placeholder='Select a category'
                showSearch
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              >
                {GlobalServicesEnums.categories.map((category) => (
                  <Option key={category.value} value={category.value}>
                    {category.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              label='Description'
              name='description'
              rules={[
                { required: true, message: 'Please enter description' },
                { min: 10, message: 'Description must be at least 10 characters' }
              ]}
            >
              <TextArea rows={4} placeholder='Enter detailed description of the service' />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label='Default Rate'
              name='defaultRate'
              rules={[
                { required: true, message: 'Please enter default rate' },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.reject(new Error('Please enter default rate'))
                    }
                    const numValue = Number(value)
                    if (isNaN(numValue) || numValue < 0) {
                      return Promise.reject(new Error('Rate must be a positive number'))
                    }
                    return Promise.resolve()
                  }
                }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder='0.00'
                min={0}
                step={0.01}
                precision={2}
                formatter={(value) => `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value.replace(/R\s?|(,*)/g, '')}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label='Default Duration (Minutes)'
              name='defaultDurationMin'
              rules={[
                { required: true, message: 'Please enter duration in minutes' },
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.reject(new Error('Please enter duration in minutes'))
                    }
                    const numValue = Number(value)
                    if (isNaN(numValue) || numValue < 1) {
                      return Promise.reject(new Error('Duration must be at least 1 minute'))
                    }
                    return Promise.resolve()
                  }
                }
              ]}
            >
              <InputNumber style={{ width: '100%' }} placeholder='0' min={1} step={15} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label='Active Status'
              name='active'
              valuePropName='checked'
              extra='Toggle to activate/deactivate this service'
            >
              <Switch checkedChildren='Active' unCheckedChildren='Inactive' defaultChecked={true} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item>
          <Space>
            <Button type='primary' htmlType='submit' icon={<SaveOutlined />} loading={formLoading} size='large'>
              {currentRecord?.isNew ? 'Create Service' : 'Update Service'}
            </Button>
            <Button onClick={handleCancel} size='large'>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  )
}

export default GlobalServicesForm
